import React, { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axiosInstance';
import { getAuthToken, logout } from '../auth/cookieUtils';
import './dashboard.css';
import trident from '../assets/odin-trident.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import Sidebar from '../Components/Sidebar';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DASHBOARD_ASSET_COLUMNS = [
  ['equipmentName', 'Equipment Name'],
  ['equipmentNumber', 'Equipment Number'],
  ['alternateNumber', 'Alternate Number'],
  ['locationMillNumber', 'Mill Number'],
  ['equipmentType', 'Equipment Type'],
  ['facility', 'Facility'],
  ['materialType', 'Material Type'],
  ['inspectionFrequency', 'Inspection Frequency'],
  ['riskRating', 'Risk Rating'],
  ['nextExternalInspection', 'Next External Inspection'],
  ['nextInternalInspection', 'Next Internal Inspection'],
  ['nextUtInspection', 'Next UT Inspection']
];

const EMPTY_DASHBOARD_SUMMARY = {
  externalOutOfCompliance: 0,
  internalOutOfCompliance: 0,
  utOutOfCompliance: 0,
  fullyCompliant: 0,
  totalAssets: 0,
};


const Dashboard = () => {
  const navigate = useNavigate();
  const token = getAuthToken();

  
  const [areas, setAreas] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [summary, setSummary] = useState(EMPTY_DASHBOARD_SUMMARY);
  
  const [equipmentList, setEquipmentList] = useState([]);
  const [inspectionCostData, setInspectionCostData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [xAxisScale, setXAxisScale] = useState('month');
  const [sortConfig, setSortConfig] = useState({ key: 'equipmentName', direction: 'asc' });

  function getDefaultStartDate() {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  }

  const displayNext = (dateVal, overrideVal) => {
  const o = (overrideVal ?? '').toString().trim();
  return dateVal || (o.length ? o : '—');
};
const getAreaName = (equipment) => {
  if (equipment.areaName) return equipment.areaName;
  if (equipment.area?.name) return equipment.area.name;

  const matchingArea = areas.find(
    area => String(area.id) === String(equipment.areaId)
  );

  return matchingArea?.name || '—';
};

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  useEffect(() => {
    if (!selectedLocationId) {
      setAreas([]);
      setSelectedAreas([]);
      return;
    }
    const fetchAreas = async () => {
      try {
        const response = await axios.get(`/api/areas/location/${selectedLocationId}`);
        setAreas(Array.isArray(response.data) ? response.data : []);
        setSelectedAreas([]);
      } catch (error) {
        console.error('Error fetching areas:', error);
        setAreas([]);
        setSelectedAreas([]);
        if ([401, 403].includes(error.response?.status)) {
          logout();
        }
      }
    };
    fetchAreas();
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) {
      setSummary(EMPTY_DASHBOARD_SUMMARY);
      return;
    }
    const fetchSummary = async () => {
      try {
        let url = `/api/dashboard-summary?locationId=${selectedLocationId}`;
        selectedAreas.forEach(areaId => url += `&areaIds=${areaId}`);
        const response = await axios.get(url);
        setSummary({
          ...EMPTY_DASHBOARD_SUMMARY,
          ...(response.data && typeof response.data === 'object' ? response.data : {}),
        });
      } catch (error) {
        console.error('Error fetching summary:', error);
        setSummary(EMPTY_DASHBOARD_SUMMARY);
      }
    };
    fetchSummary();
  }, [selectedLocationId, selectedAreas]);

  useEffect(() => {
    if (!selectedLocationId) {
      setInspectionCostData([]);
      return undefined;
    }

    let ignoreResponse = false;

    const fetchInspectionCosts = async () => {
      try {
        let url = `/api/inspection-costs/aggregated?locationId=${selectedLocationId}&startDate=${startDate}&endDate=${endDate}&scale=${xAxisScale}`;
        selectedAreas.forEach(areaId => url += `&areaIds=${areaId}`);

        const response = await axios.get(url);
        const formattedData = (Array.isArray(response.data) ? response.data : []).map(item => {
          const periodDate = new Date(item.period + 'T00:00:00Z');
          return {
            ...item,
            periodLabel: xAxisScale === 'month'
              ? periodDate.toLocaleString('default', { month: 'short', year: 'numeric', timeZone: 'UTC' })
              : periodDate.getUTCFullYear(),
          };
        });

        if (!ignoreResponse) setInspectionCostData(formattedData);
      } catch (error) {
        if (!ignoreResponse) {
          console.error('Error fetching inspection costs:', error);
          setInspectionCostData([]);
        }
      }
    };

    fetchInspectionCosts();

    return () => {
      ignoreResponse = true;
    };
  }, [selectedLocationId, selectedAreas, startDate, endDate, xAxisScale]);

  useEffect(() => {
    if (!selectedLocationId) {
      setEquipmentList([]);
      setTotalPages(0);
      return undefined;
    }

    let ignoreResponse = false;

    const fetchEquipmentDescriptions = async () => {
      try {
        let url = `/api/equipment/descriptions/paginated?locationId=${selectedLocationId}&page=${currentPage}&size=${pageSize}&sortBy=${sortConfig.key}&sortDirection=${sortConfig.direction}`;
        selectedAreas.forEach((areaId) => (url += `&areaIds=${areaId}`));

        const response = await axios.get(url);
        if (!ignoreResponse) {
          setEquipmentList(Array.isArray(response.data?.content) ? response.data.content : []);
          setTotalPages(Number.isInteger(response.data?.totalPages) ? response.data.totalPages : 0);
        }
      } catch (error) {
        if (!ignoreResponse) {
          console.error('Error fetching equipment descriptions:', error);
          setEquipmentList([]);
          setTotalPages(0);
        }
      }
    };

    fetchEquipmentDescriptions();

    return () => {
      ignoreResponse = true;
    };
  }, [selectedLocationId, selectedAreas, currentPage, pageSize, sortConfig]);

  useEffect(() => {
    setCurrentPage(0);
  }, [startDate, endDate, xAxisScale]);

  const handleLogout = () => {
    logout();
  };

  const toggleAreaSelection = (areaId) => {
    setCurrentPage(0);
    setSelectedAreas((prev) =>
      prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
    );
  };

  const handleSort = (key) => {
    setCurrentPage(0);
    setSortConfig((prevConfig) => {
      return prevConfig.key === key
        ? { key, direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' };
    });
  };
  

  const exportOutOfComplianceToExcel = async (inspectionType) => {
  if (!selectedLocationId) return;

  try {
    let url = `/api/equipment/descriptions/out-of-compliance?locationId=${selectedLocationId}&inspectionType=${inspectionType}`;

    if (selectedAreas.length > 0) {
      selectedAreas.forEach(areaId => {
        url += `&areaIds=${areaId}`;
      });
    }

    const response = await axios.get(url);
    const data = response.data || [];
    if (data.length === 0) {
  alert('No out-of-compliance equipment found for this category.');
  return;
}

    const formattedData = data.map((equipment) => ({
      'Equipment Name': equipment.equipmentName,
      'Equipment Number': equipment.equipmentNumber,
      'Alternate Number': equipment.alternateNumber,
      'Area': getAreaName(equipment),
      'Equipment Type': equipment.equipmentType,
      'Material Type': equipment.materialType,
      'Risk Rating': equipment.riskRating,
      'Next External Inspection': displayNext(equipment.nextExternalInspection, equipment.nextExternalOverride),
      'Next Internal Inspection': displayNext(equipment.nextInternalInspection, equipment.nextInternalOverride),
      'Next UT Inspection': displayNext(equipment.nextUtInspection, equipment.nextUtOverride),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    if (formattedData.length > 0) {
      const headers = Object.keys(formattedData[0]);
      const lastCol = headers.length - 1;
      const lastRow = formattedData.length;

      worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: lastRow, c: lastCol },
        }),
      };

      worksheet['!cols'] = headers.map(() => ({ wch: 24 }));
    }

    const workbook = XLSX.utils.book_new();

    const sheetName =
      inspectionType === 'external'
        ? 'External Out of Compliance'
        : inspectionType === 'internal'
          ? 'Internal Out of Compliance'
          : 'UT Out of Compliance';

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileName =
      inspectionType === 'external'
        ? 'external_out_of_compliance.xlsx'
        : inspectionType === 'internal'
          ? 'internal_out_of_compliance.xlsx'
          : 'ut_out_of_compliance.xlsx';

    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      fileName
    );
  } catch (error) {
    console.error(`Error exporting ${inspectionType} out-of-compliance equipment:`, error);
    alert('Failed to export out-of-compliance equipment.');
  }
};



  const exportToPDF = async () => {
    if (!selectedLocationId) {
      alert('Select a location before exporting equipment.');
      return;
    }

    try {
      const doc = new jsPDF();
      doc.text('Equipment Descriptions', 14, 10);

      const tableColumns = [
        'Equipment Name',
        'Equipment Number',
        'Alternate Number',
        'Mill Number',
        'Equipment Type',
        'Facility',
        'Material Type',
        'Inspection Frequency',
        'Risk Rating',
        'Next External Inspection',
        'Next Internal Inspection',
        'Next UT Inspection',
      ];

      let url = `/api/equipment/descriptions/export?locationId=${selectedLocationId}&sortBy=${sortConfig.key}&sortDirection=${sortConfig.direction}`;
      if (selectedAreas.length > 0) {
        selectedAreas.forEach(areaId => url += `&areaIds=${areaId}`);
      }
      const response = await axios.get(url);
      const fullEquipmentList = Array.isArray(response.data) ? response.data : [];

      if (fullEquipmentList.length === 0) {
        alert('There is no equipment to export for the selected filters.');
        return;
      }

      const tableRows = fullEquipmentList.map((equipment) => [
        equipment.equipmentName,
        equipment.equipmentNumber,
        equipment.alternateNumber,
        equipment.locationMillNumber,
        equipment.equipmentType,
        equipment.facility,
        equipment.materialType,
        equipment.inspectionFrequency,
        equipment.riskRating,
        displayNext(equipment.nextExternalInspection, equipment.nextExternalOverride),
        displayNext(equipment.nextInternalInspection, equipment.nextInternalOverride),
        displayNext(equipment.nextUtInspection, equipment.nextUtOverride),
      ]);

      autoTable(doc, {
        head: [tableColumns],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
      });

      doc.save('Equipment_Descriptions.pdf');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Unable to export equipment to PDF. Please try again.');
    }
  };

  return (
    <div className="dashboard-page dashboard-home-page">
      <div className="dashboard-topbar">
      <div className="topbar-left">
  <img src={trident} alt="ODIN Logo" className="navbar-logo" />
  <CompanyLocationSelector
    onLocationChange={(locationId) => setSelectedLocationId(locationId)}
  />
</div>
        <div className="topbar-center">
          <span className="page-title">Dashboard</span>
        </div>
        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
    <div className="dashboard-sidebar-and-filters">
      <Sidebar />
    </div>
            
<div className="dashboard-content">

<section className="dashboard-filters dashboard-area-filter" aria-label="Filter dashboard by area">
  <div className="dashboard-area-filter-header">
    <div className="dashboard-area-filter-copy">
      <h3>Area filter</h3>
      <p>
        {selectedAreas.length > 0
          ? `${selectedAreas.length} ${selectedAreas.length === 1 ? 'area' : 'areas'} selected — all dashboard results are filtered`
          : 'All areas — selections apply to the compliance tiles, charts, and equipment table'}
      </p>
    </div>
    {selectedAreas.length > 0 && (
      <button
        type="button"
        className="dashboard-clear-area-filter"
        onClick={() => {
          setCurrentPage(0);
          setSelectedAreas([]);
        }}
      >
        Clear selection
      </button>
    )}
  </div>
  {areas.length > 0 ? (
    <ul className="area-list">
      {areas.map((area) => {
        const isSelected = selectedAreas.includes(area.id);
        return (
          <li key={area.id}>
            <button
              type="button"
              className={`area-filter-option${isSelected ? ' selected' : ''}`}
              aria-pressed={isSelected}
              onClick={() => toggleAreaSelection(area.id)}
            >
              {area.name}
            </button>
          </li>
        );
      })}
    </ul>
  ) : (
    <p className="dashboard-area-filter-empty">No areas are available for the selected location.</p>
  )}
</section>

<div className="dashboard-widgets">
  <button
  type="button"
  className="widget clickable-widget"
  disabled={!selectedLocationId}
  onClick={() => exportOutOfComplianceToExcel('external')}
  title="Download externally out-of-compliance equipment"
>
  <div className="number red">{summary.externalOutOfCompliance}</div>
  <div>Externally Out of Compliance</div>
  <div className="widget-download-hint">Click to download list</div>
</button>

<button
  type="button"
  className="widget clickable-widget"
  disabled={!selectedLocationId}
  onClick={() => exportOutOfComplianceToExcel('internal')}
  title="Download internally out-of-compliance equipment"
>
  <div className="number red">{summary.internalOutOfCompliance}</div>
  <div>Internally Out of Compliance</div>
  <div className="widget-download-hint">Click to download list</div>
</button>

<button
  type="button"
  className="widget clickable-widget"
  disabled={!selectedLocationId}
  onClick={() => exportOutOfComplianceToExcel('ut')}
  title="Download UT out-of-compliance equipment"
>
  <div className="number orange">{summary.utOutOfCompliance}</div>
  <div>UT Out of Compliance</div>
  <div className="widget-download-hint">Click to download list</div>
</button>

  <div className="widget">
    <div className="number green">{summary.fullyCompliant}</div>
    Fully Compliant
  </div>

  <div className="widget">
    <div className="number white">{summary.totalAssets}</div>
    Total Number of Assets
  </div>
</div>
          
          <div className="dashboard-charts-and-filters">
            <div className="dashboard-charts-wrapper">
              <div className="dashboard-row">
                <div className="chart">
                  <h4>Compliance Overview</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Fully Compliant', value: summary.fullyCompliant },
                          { name: 'Out of Compliance', value: Math.max(summary.totalAssets - summary.fullyCompliant, 0) },
                        ]}
                        cx="50%" cy="50%" innerRadius={40} outerRadius={85} paddingAngle={2} labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} dataKey="value"
                      >
                        <Cell fill="#4caf50" />
                        <Cell fill="#ff4c4c" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart">
                  <h4>Compliance Breakdown</h4>
                  <ResponsiveContainer width="100%" height={250}>
                  <BarChart
  data={[{
    name: 'Equipment',
    external: summary.externalOutOfCompliance,
    internal: summary.internalOutOfCompliance,
    ut: summary.utOutOfCompliance,
    compliant: summary.fullyCompliant
  }]}
>
  <CartesianGrid strokeDasharray="3 3" />
  <XAxis dataKey="name" tick={{ fill: '#ccc' }} />
  <YAxis tick={{ fill: '#ccc' }} />
  <Tooltip />
  <Legend />
  <Bar dataKey="external" fill="#ff4c4c" name="Externally Out of Compliance" />
  <Bar dataKey="internal" fill="#ffcc00" name="Internally Out of Compliance" />
  <Bar dataKey="ut" fill="#ff9800" name="UT Out of Compliance" />
  <Bar dataKey="compliant" fill="#4caf50" name="Fully Compliant" />
</BarChart>

                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart inspection-cost-chart">
                <div className="chart-header">
                  <h4>Inspection Costs</h4>
                  <div className="filters-inside-chart">
                    <div className="date-range-picker">
                      <label>Start:</label>
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                      <label>End:</label>
                      <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div className="scale-selector">
                      <label>Scale:</label>
                      <select value={xAxisScale} onChange={(e) => setXAxisScale(e.target.value)}>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                      </select>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={inspectionCostData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodLabel" tick={{ fill: '#ccc' }} />
                    <YAxis tick={{ fill: '#ccc' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="inspectionCost" stroke="#42a5f5" name="Inspection Cost" />
                    <Line type="monotone" dataKey="scaffoldCost" stroke="#ff9800" name="Scaffold Cost" />
                    <Line type="monotone" dataKey="cleaningCost" stroke="#66bb6a" name="Cleaning Cost" />
                    <Line type="monotone" dataKey="repairCost" stroke="#ef5350" name="Repair Cost" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            
          </div>

          <div className="equipment-table-section">
            <div className="equipment-table-header">
              <div className="equipment-table-heading-copy">
                <span className="equipment-table-eyebrow">Assets database</span>
                <h4 className="equipment-table-title">Equipment inventory</h4>
                <p className="equipment-table-subtitle">
                  {equipmentList.length} {equipmentList.length === 1 ? 'asset' : 'assets'} on this page · Select a row to open its profile
                </p>
              </div>
              <button type="button" onClick={exportToPDF} className="export-pdf-button" disabled={!selectedLocationId}>
                Export to PDF
              </button>
            </div>
            <div className="table-scroll-container">
              <table className="equipment-table">
                <thead>
                  <tr>
                    {DASHBOARD_ASSET_COLUMNS.map(([key, label]) => {
                      const isActiveSort = sortConfig.key === key;
                      return (
                        <th
                          key={key}
                          aria-sort={isActiveSort ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button
                            type="button"
                            className={`equipment-table-sort${isActiveSort ? ' active' : ''}`}
                            onClick={() => handleSort(key)}
                          >
                            <span>{label}</span>
                            <span className="equipment-table-sort-icon" aria-hidden="true">
                              {isActiveSort ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {equipmentList.length > 0 ? equipmentList.map((equipment) => (
                      <tr
                        key={equipment.equipmentId}
                        onClick={() => navigate(`/equipment/${equipment.equipmentId}/profile`)}
                        className="clickable-row"
                      >
                        <td className="equipment-name-cell">{equipment.equipmentName || '—'}</td>
                        <td>{equipment.equipmentNumber || '—'}</td>
                        <td>{equipment.alternateNumber || '—'}</td>
                        <td>{equipment.locationMillNumber || '—'}</td>
                        <td>{equipment.equipmentType || '—'}</td>
                        <td>{equipment.facility || '—'}</td>
                        <td>{equipment.materialType || '—'}</td>
                        <td>{equipment.inspectionFrequency || '—'}</td>
                        <td><span className="risk-rating-value">{equipment.riskRating || '—'}</span></td>
                        <td>{displayNext(equipment.nextExternalInspection, equipment.nextExternalOverride)}</td>
                        <td>{displayNext(equipment.nextInternalInspection, equipment.nextInternalOverride)}</td>
                        <td>{displayNext(equipment.nextUtInspection, equipment.nextUtOverride)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td className="equipment-table-empty" colSpan={DASHBOARD_ASSET_COLUMNS.length}>
                          No assets match the selected dashboard filters.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
            <div className="pagination-controls">
              <button type="button" disabled={currentPage === 0 || totalPages === 0} onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
              <span>Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}</span>
              <button type="button" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
